/**
 * HydraEndpoint — Stores Hydra chain metadata without propagation.
 *
 * HydraRenderer builds chains from GraphIndex directly — endpoint-to-endpoint
 * propagation is not needed.
 *
 * connectTo registers a HydraRenderer when connecting to a VideoEndpoint.
 * onConnectedFrom captures external video canvas sources.
 */

import type { IEndpoint } from '@/runtime/IEndpoint';
import type { INode } from '@/nodes/INode';
import { type DataTypeSpec, type DataFlow } from '@/nodes/Endpoint';
import { VideoEndpoint } from '@plugins/video/VideoEndpoint';
import type { EndpointWithContext } from '@plugins/Plugin';
import { logger } from '@/logging';

import type { Payload } from '@/graph/Payload';
import { registerVideoRenderer, unregisterVideoEntry, getCanvasFromVideoRegistry } from '@plugins/video/videoRegistry';
import { HydraRenderer } from './HydraRenderer';
import type { IRuntimeStore, StoreKey } from '@/runtime/RuntimeStore';
import type { GraphIndex } from '@/runtime/GraphIndex';



export interface HydraPayload extends Payload<HydraChain> {

}
/**
 * Describes a Hydra function to be applied.
 * Source: { source: true, name: 'osc', args: [() => freq, () => sync] }
 * Effect: { effect: true, name: 'rotate', args: [() => angle] }
 * Composite: { composite: true, name: 'add', args: [secondaryChain, () => amount] }
 * External canvas: { source: true, external: true, context: CanvasRenderingContext2D }
 */
export interface HydraFunctionMeta {
  source?: boolean;
  effect?: boolean;
  composite?: boolean;
  newSource?: { type: 'webcam' | 'file' | 'canvas', data?: any };
  name?: string;
  args?: any[];
}


/**
 * An array of function metadata that will be evaluated by the canvas node.
 */
export type HydraChain = HydraFunctionMeta[];

export class HydraEndpoint implements IEndpoint {
  readonly id: string;
  readonly dataType = 'hydra' as DataTypeSpec;
  readonly dataFlow = 'internal' as DataFlow;
  readonly instancePath: string;
  readonly isInput!: boolean;
  readonly isOutput!: boolean;

  // Optional config fields (set via Object.assign)
  readonly label?: string;
  readonly primary?: boolean;
  readonly group?: string;
  readonly groupOrder?: number;
  readonly disabled?: boolean;
  readonly transient?: boolean;

  node: INode;

  // Connection tracking (no ongoing propagation)
  private targetEndpoints: (HydraEndpoint | VideoEndpoint | IEndpoint)[] = [];
  internalEndpoint: IEndpoint | null = null;

  renderers: HydraRenderer[] = [];
  Hydra: any;
  private graphIndex: GraphIndex;

  constructor(ctx: EndpointWithContext, store: IRuntimeStore, Hydra: any) {
    this.instancePath = ctx.instancePath;
    this.node = ctx.node;
    this.graphIndex = ctx.graphIndex;
    // Apply all config fields (isInput, isOutput, label, group, etc.)
    Object.assign(this, ctx.endpoint);
    this.id = ctx.endpoint.id!;
    this.Hydra = Hydra;
  }

  get storeKey(): StoreKey {
    return {
      instancePath: this.instancePath,
      nodeId: this.node.id,
      endpointId: this.id
    };
  }

  getValue(): Payload<HydraChain> {
    return { value: [] };
  }

  setValue(value: HydraChain | HydraPayload, source?: IEndpoint): void {
    const chain = Array.isArray(value) ? value : value?.value;

    if (!source || source !== (this as IEndpoint)) {
      this.notifyNode({ value: chain } as HydraPayload, source);
    }

    // No propagation — HydraRenderer builds chain from GraphIndex directly.
  }

  receive(payload: Payload, source: IEndpoint): void {
    this.setValue(payload, source);
  }

  // ─────────────────────────────────────────────────────────────
  // Connections
  // ─────────────────────────────────────────────────────────────

  connectTo(target: IEndpoint): void {
    if (!this.targetEndpoints.includes(target as any)) {
      this.targetEndpoints.push(target as any);
    }

    logger.trace('HydraEndpoint', 'connectTo', target.constructor.name, target.node.id, 'isVideo:', target instanceof VideoEndpoint);
    if (target instanceof VideoEndpoint) {
      // Register this hydra renderer against the video node
      logger.trace('HydraEndpoint', `registering hydra renderer at ${target.instancePath}.${target.node.id}`);
      const key = `${target.instancePath}.${target.node.id}`;
      const newRenderer = new HydraRenderer(this.Hydra, key);
      // Configure graph-query mode: renderer builds chain from graph structure
      newRenderer.setGraphSource(this.graphIndex, this.node.id, this.instancePath);
      this.renderers.push(newRenderer);
      registerVideoRenderer(target.instancePath, target.node.id, newRenderer);
    }
  }

  async onConnectedFrom(source: IEndpoint) {
    logger.trace('HydraEndpoint', 'onConnectedFrom', source.constructor.name, source.node.id);
    if (source instanceof VideoEndpoint) {
      try {
        const canvas = await getCanvasFromVideoRegistry(source);
        const chain: HydraChain = [{ newSource: { type: 'canvas', data: canvas } }];
        this.setValue(chain, source);
      } catch (e) {
        logger.error('source canvas not available');
      }
    }
  }

  disconnectFrom(target: IEndpoint): void {
    if (target instanceof VideoEndpoint) {
      unregisterVideoEntry(target.instancePath, target.node.id);
      const key = `${target.instancePath}.${target.node.id}`;
      this.renderers = this.renderers.filter(r => r.key !== key);
    }

    const idx = this.targetEndpoints.indexOf(target as any);
    if (idx !== -1) {
      this.targetEndpoints.splice(idx, 1);
    }
  }

  /** Set internal endpoint for subgraph boundary wiring. */
  connectInternal(internal: IEndpoint): void {
    this.internalEndpoint = internal;
  }

  setConnectionDisabled(_target: IEndpoint, _disabled: boolean): void {
    // No-op: HydraRenderer builds chain from GraphIndex
  }

  dispose(): void {
    for (const target of this.targetEndpoints) {
      if (target instanceof VideoEndpoint) {
        unregisterVideoEntry(target.instancePath, target.node.id);
      }
    }
    this.renderers = [];
    this.targetEndpoints = [];
    this.internalEndpoint = null;
  }

  /** Dispatch to appropriate node handler based on dataFlow */
  private notifyNode(payload: any, source?: IEndpoint): void {
    switch (this.dataFlow) {
      case 'param':
        this.node.onParamChanged(payload, this.id, source);
        break;
      case 'internal':
        this.node.onInternalChanged(payload, this.id, source);
        break;
      case 'event':
        this.node.handleEvent(payload, this.id, source);
        break;
      case 'trigger':
        this.node.handleTrigger(payload, this.id, source);
        break;
    }
  }
}
